import {z as zod} from "zod";
import {PaymentCurrency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentCurrency";
import {PaymentFrequency} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/subscriptionWorkspaceCapability/domain/PaymentFrequency";
import type {Integer, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {integerZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";

export type SubscriptionPlanPricing = {
    crp: Integer;
    currency: PaymentCurrency;
    frequency: PaymentFrequency;
    mrp: Integer;
};

export type SubscriptionPlan = {
    copilotConfig: Record<string, unknown>;
    id: Uuid;
    maxCopilotTokensPerHour: Integer;
    maxCreatorCount: Integer;
    maxL0Count: Integer;
    maxUserCount: Integer;
    maxViewerCount: Integer;
    pricesPerMonth: Array<SubscriptionPlanPricing>;
};

export type PublicSubscriptionPlan = Omit<SubscriptionPlan, "copilotConfig" | "maxCopilotTokensPerHour"> & {
    displayName: string;
    featureTagText: string;
    features: Array<string>;
    isFeatured?: boolean;
    tagText: string;
};

export type SubscriptionPlanWithPublicDetails = SubscriptionPlan & {
    displayName: string;
    featureTagText: string;
    features: Array<string>;
    tagText: string;
};

export const subscriptionPlanPricingZodValidator = zod.object({
    crp: integerZodValidator,
    currency: zod.enum(PaymentCurrency),
    frequency: zod.enum(PaymentFrequency),
    mrp: integerZodValidator,
});

export const subscriptionPlanZodValidator = zod.object({
    copilotConfig: zod.record(zod.string(), zod.unknown()),
    id: uuidZodValidator,
    maxCopilotTokensPerHour: integerZodValidator,
    maxCreatorCount: integerZodValidator,
    maxL0Count: integerZodValidator,
    maxUserCount: integerZodValidator,
    maxViewerCount: integerZodValidator,
    pricesPerMonth: zod.array(subscriptionPlanPricingZodValidator),
});

export const publicSubscriptionPlanZodValidator = subscriptionPlanZodValidator
    .omit({
        copilotConfig: true,
        maxCopilotTokensPerHour: true,
    })
    .extend({
        displayName: zod.string(),
        featureTagText: zod.string(),
        features: zod.array(zod.string()),
        isFeatured: zod.boolean().optional(),
        tagText: zod.string(),
    });

export const subscriptionPlanWithPublicDetailsZodValidator = subscriptionPlanZodValidator.extend({
    displayName: zod.string(),
    featureTagText: zod.string(),
    features: zod.array(zod.string()),
    tagText: zod.string(),
});
